import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { AddSynonymModal } from '../components/synonyms/add-synonym-modal';
import { AdminHubHeader } from '../components/admin-hub/admin-hub-header';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { DeleteSynonymModal } from '../components/synonyms/delete-synonym-modal';
import { EditSynonymModal } from '../components/synonyms/edit-synonym-modal';
import { fetchSynonyms, useSynonymsDispatch, useSynonymsState } from '@packages/contexts/synonyms';
import { FetchSynonymsOptions, Synonym } from '@packages/models/api/synonyms-search-term';
import { find } from 'lodash';
import { Pagination } from '@web/components/pagination';
import { SORT_DIRECTION, TableCell, TableHeader, TableRenderer, TableRow } from '@web/components/table';
import { StyleSheet } from 'react-native';
import { synonymsService } from '@web/services/singletons';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';
import RecentSearchTypeahead from '@web/components/recent-search-typeahead';
import ToastNotification from '@web/components/toast-notification';

const HEADER_TITLES = [
    { key: 'searchTerm', label: 'Search Term', sortable: true, width: '20%' },
    { key: 'symptoms', label: 'Synonyms', sortable: false, width: '70%' },
    { key: 'actions', label: 'Actions', sortable: false, width: '10%' },
];

interface SynonymsTableRowModel {
    rowId: string;
    searchTerm: string;
    synonyms: string[];
}
interface SynonymTableRowProps {
    rowData: SynonymsTableRowModel;
    handleDelete: (id?: string) => void;
    handleEdit: (id?: string) => void;
}

const useStyles = createUseStyles({
    container: {
        minHeight: '86vh',
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },
    outerTableEmpty: {
        height: '50vh',
        minHeight: 'auto',
    },
    tableCell: {
        padding: '16px',
    },
    actionIcons: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        padding: '10px',
    },
    pageFooter: {
        borderTop: '1px solid #ddd',
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    synonymsTable: {
        marginTop: '20px',
    },
});

const styles = StyleSheet.create({
    tableHeader: {
        fontWeight: '500',
    },
    addSynonymsButton: {
        display: 'flex',
        alignItems: 'center',
    },
    emptyStateDescriptionTitle: {
        fontWeight: '600',
        fontSize: 21,
        lineHeight: 32,
        display: 'flex',
        justifyContent: 'center',
    },
    emptyStateDescriptionBody: {
        fontWeight: '400',
        fontSize: 15,
        lineHeight: 24,
        display: 'flex',
        justifyContent: 'center',
    },
    emptyStateIcon: {
        backgroundColor: colors.grayFive,
        borderRadius: 50,
    },
    addSynonymPlusIcon: {
        backgroundColor: colors.white,
        borderRadius: 50,
    },
});

const EmptyStateDescription: FC<{ textSearch?: string }> = ({ textSearch }) => {
    return (
        <>
            <Typography color="textDarkSecondary" style={styles.emptyStateDescriptionTitle}>
                {textSearch ? 'No Results Found' : ' No synonym added yet'}
            </Typography>
            <Typography color="textDarkSecondary" style={styles.emptyStateDescriptionBody}>
                Add synonyms linked to search terms to improve search engine performance
            </Typography>
        </>
    );
};

interface SynonymsTableHeaderProps {
    onSort: (data: FetchSynonymsOptions) => void;
    sort: string;
    sortDirection: string;
}

const SynonymsTableHeader = memo(({ onSort, sort, sortDirection }: SynonymsTableHeaderProps) => {
    return (
        <TableRow>
            {HEADER_TITLES.map(({ key, label, sortable, width }) => (
                <TableHeader
                    key={key}
                    width={width}
                    sortable={sortable}
                    sortDirection={sort === key ? (sortDirection as SORT_DIRECTION) : null}
                    onSort={(_, direction) => onSort({ direction: direction, sort: key })}
                >
                    <Typography style={styles.tableHeader} numberOfLines={1} lineBreakMode="tail" ellipsizeMode="tail">
                        {label}
                    </Typography>
                </TableHeader>
            ))}
        </TableRow>
    );
});

const SynonymsTableRow: FC<SynonymTableRowProps> = ({ rowData, handleEdit, handleDelete }) => {
    const { rowId, searchTerm, synonyms } = rowData;

    const classes = useStyles();

    return (
        <TableRow key={rowId}>
            <TableCell className={classes.tableCell}>
                <Typography>{searchTerm}</Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography>{synonyms?.join(', ')}</Typography>
            </TableCell>
            <TableCell className={classes.actionIcons}>
                <Button variant="ghost-blue" onPress={() => handleEdit(rowId)}>
                    <Icon name="pencil" color="blueOne" size={24} />
                </Button>
                <Button variant="ghost-blue" onPress={() => handleDelete(rowId)}>
                    <Icon name="trash-striped" color="blueOne" size={24} />
                </Button>
            </TableCell>
        </TableRow>
    );
};

export const Synonyms: FC = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const synonymDispatch = useSynonymsDispatch();
    const { synonyms, pagination, loading: isSynonymsLoading } = useSynonymsState();
    const [selectedSynonym, setSelectedSynonym] = useState<Synonym>();
    const totalPages = Math.ceil(pagination.total / pagination.size);
    const [showToast, setShowToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
    const [sortBy, setSortBy] = useState('updatedAt');
    const [sortDirection, setSortDirection] = useState('desc');

    const [modals, setModals] = useState({
        add: false,
        edit: false,
        delete: false,
    });
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState<string | undefined>(undefined);

    const toggleModal = (type: 'add' | 'edit' | 'delete', synonymId?: string) => {
        setModals((prev) => ({ ...prev, [type]: !prev[type] }));
        if (synonymId) {
            const synonym = find(synonyms, { searchTermId: synonymId });
            setSelectedSynonym(synonym);
        } else {
            // Clear selected synonym when closing modal
            setSelectedSynonym(undefined);
        }
    };

    const showToastMessage = (message: string) => {
        setShowToast({ show: true, message });
    };

    const handleSortChange = useCallback(({ sort, direction }: FetchSynonymsOptions) => {
        if (!sort || !direction) return;
        setSortBy(sort);
        setSortDirection(direction);
    }, []);

    useEffect(() => {
        fetchSynonyms(synonymDispatch, synonymsService, {
            page,
            size: itemsPerPage,
            query: searchText,
            sort: sortBy,
            direction: sortDirection,
        });
    }, [page, itemsPerPage, searchText, sortBy, sortDirection]);

    return (
        <>
            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name="checkmark" size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={2000}
                variant="success"
                position="top-center"
            />
            <div className={`d-flex flex-column ${classes.container}`}>
                <AdminHubHeader
                    breadcrumbs={[
                        {
                            title: t('administration:breadcrumbs.adminHub', 'Administration Hub'),
                            to: '/administration',
                        },
                        { title: t('synonyms:breadcrumbs.synonyms', 'Synonyms') },
                    ]}
                />
                <div className="container-fluid mt-4">
                    <div className="row mb-3">
                        <div className="col-12 col-md-6">
                            <RecentSearchTypeahead
                                onChange={(value: string) => {
                                    setSearchText(value);
                                    setPage(1);
                                    setItemsPerPage(10);
                                }}
                                placeholder={t('Search for a synonym')}
                                defaultInputValue={searchText}
                                recentSearches={[]}
                                quickSearchQuery={searchText || ''}
                                searchIconColor="grayFive"
                            />
                        </div>
                        <div className="col-12 col-md-6 text-md-end d-flex justify-content-end">
                            <Button
                                style={styles.addSynonymsButton}
                                variant="primary"
                                title={t('Add new synonym')}
                                iconRight={
                                    <Icon name="plus" color="blueOne" size={19.5} style={styles.addSynonymPlusIcon} />
                                }
                                onPress={() => toggleModal('add')}
                            />
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-12">
                            <TableRenderer<SynonymsTableRowModel>
                                isLoading={isSynonymsLoading}
                                tableRowsData={synonyms.map(
                                    (synonym) =>
                                        ({
                                            rowId: synonym.searchTermId,
                                            searchTerm: synonym.searchTerm,
                                            synonyms: synonym.synonyms,
                                        }) as SynonymsTableRowModel
                                )}
                                tableRowsDataSetter={() => {}}
                                tableHeaderRowRenderer={() => (
                                    <SynonymsTableHeader
                                        onSort={handleSortChange}
                                        sort={sortBy}
                                        sortDirection={sortDirection}
                                    />
                                )}
                                tableBodyRowRenderer={(rowData) => (
                                    <SynonymsTableRow
                                        handleEdit={(id) => toggleModal('edit', id)}
                                        handleDelete={(id) => toggleModal('delete', id)}
                                        rowData={rowData}
                                    />
                                )}
                                emptyStateProps={{
                                    icon: 'plus',
                                    description: <EmptyStateDescription textSearch={searchText} />,
                                    iconProps: { size: 65, color: 'white', style: styles.emptyStateIcon },
                                }}
                                outerTableClassName={synonyms.length ? classes.outerTable : classes.outerTableEmpty}
                            />
                        </div>
                    </div>
                </div>

                <AddSynonymModal
                    show={modals.add}
                    onHide={() => toggleModal('add')}
                    showToastMessage={showToastMessage}
                    handleSortChange={handleSortChange}
                />
                <EditSynonymModal
                    show={modals.edit}
                    onHide={() => toggleModal('edit')}
                    selectedSynonym={selectedSynonym}
                    showToastMessage={showToastMessage}
                    handleSortChange={handleSortChange}
                />
                <DeleteSynonymModal
                    show={modals.delete}
                    onHide={() => toggleModal('delete')}
                    selectedSynonym={selectedSynonym}
                    showToastMessage={showToastMessage}
                    handleSortChange={handleSortChange}
                />
            </div>
            <footer className={`row d-flex justify-content-center align-items-center ${classes.pageFooter}`}>
                <div className="col-12">
                    <Pagination
                        currentPage={page}
                        pages={totalPages}
                        itemsPerPage={itemsPerPage}
                        itemsPerPageOptions={[10, 20, 50]}
                        onItemsPerPageChange={(itemsNumber) => {
                            setItemsPerPage(itemsNumber);
                            setPage(1);
                        }}
                        onPageChange={(page: number) => {
                            setPage(page);
                            setItemsPerPage(10);
                        }}
                    />
                </div>
            </footer>
        </>
    );
};
